import React, { useState, useMemo, useEffect, useCallback } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import TableCell from '@mui/material/TableCell'
import { format } from 'date-fns'

import PlusIcon from '@mui/icons-material/Add'

import DataListLayout from 'layouts/DataList'
import DataListTable from 'components/common/DataList/Table'

import useAddressOverrides from 'hooks/preferences/useAddressOverrides'
import useDataList from 'hooks/useDataList'
import useDataListContext from 'hooks/context/useDataListContext'
import AddressOverridesRow from 'components/common/DataList/Item/AddressOverrides'
import EditAddressOverrideModal from 'components/preferences/EditAddressOverrideModal'
import useNotification from 'hooks/context/useNotification'
import DateRangePicker from 'components/common/DateRangePicker'
import RuleDetailsFilter from 'components/preferences/RuleDetailsFilter'
import { MODULE_NAMES } from 'utils/constants'

const AddressOverrides = () => {
  const { setTotal, page, pageSize } = useDataListContext()
  const { setError, setBasicNotification } = useNotification()

  const [openEditAddressOverrideModal, setOpenEditAddressOverrideModal] =
    useState(false)
  const [addressOverride, setAddressOverride] = useState(null)
  const [filters, setFilters] = useState({})
  const [sortList, setSortList] = useState({ 'sort[0]': 'date_start:asc' })

  const hookParams = useMemo(() => {
    const params = {
      'paging[page]': page,
      'paging[page_size]': pageSize,
      ...sortList,
    }

    if (filters.date?.[0] instanceof Date) {
      params['returnall'] = false
    } else {
      params['returnall'] = true
    }

    const searchParams = []

    const { ruleDetails, ...rest } = filters

    Object.entries(rest).forEach(([key, value]) => {
      if (key === 'date' && value?.[0] instanceof Date) {
        searchParams.push(key, format(value[0], 'yyyy-MM-dd'))
      } else {
        searchParams.push(key, value)
      }
    })

    if (ruleDetails) {
      Object.values(ruleDetails).forEach((filter) => {
        const { key, value: filterValue } = filter.value
        searchParams.push('path', key, 'value', filterValue)
      })
    }

    if (searchParams.length > 0) {
      params['search'] = searchParams.join('|')
    }

    return params
  }, [filters, page, pageSize, sortList])

  const { rules, allRules, loading, total, deleteRule, mutate } =
    useAddressOverrides({
      params: hookParams,
      ruleDetailsFilter: filters.ruleDetails,
    })

  const handleRowAction = useCallback(async (row, action) => {
    if (action === 'edit') {
      setAddressOverride(row)
      setOpenEditAddressOverrideModal(true)
    }

    if (action === 'delete') {
      try {
        await deleteRule(row.id)
        setBasicNotification(`Address Override #${row.id} has been deleted!`)
      } catch (error) {
        setError(error?.response?.data?.display_message || error?.message)
      }
    }
  }, [])

  useEffect(() => {
    if (total !== undefined) {
      setTotal(total)
    }
  }, [total])

  const { dataList, columns, dataListProps } = useDataList({
    baseRowData: rules,
    presetName: 'addressOverrides',
    setSortList,
    moduleName: MODULE_NAMES.ADDRESS_OVERRIDE,
  })

  return (
    <>
      <EditAddressOverrideModal
        open={openEditAddressOverrideModal}
        setOpen={setOpenEditAddressOverrideModal}
        addressOverride={addressOverride}
        mutate={mutate}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            mb: 6,
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Button
              startIcon={<PlusIcon />}
              variant="contained"
              color="primary"
              sx={{ whiteSpace: 'nowrap', height: '40px' }}
              onClick={() => {
                setAddressOverride(null)
                setOpenEditAddressOverrideModal(true)
              }}
            >
              Create New Address Override
            </Button>
          </Box>
          <DateRangePicker
            single
            label="Active Date"
            size="small"
            value={filters.date || [undefined, undefined]}
            setValue={(value) => setFilters({ ...filters, date: value })}
            sx={{ minWidth: 325, maxWidth: 325 }}
          />
          <RuleDetailsFilter
            rules={allRules}
            value={filters.ruleDetails || []}
            onChange={(e) =>
              setFilters({ ...filters, ruleDetails: e.target.value })
            }
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Box>

        <Card sx={{ boxShadow: 2, width: '100%' }}>
          <DataListTable
            {...dataListProps}
            rowKey="id"
            dataList={dataList}
            columns={columns}
            loading={loading}
            filters={filters}
            sortList={sortList}
            setSortList={setSortList}
            RowComponent={AddressOverridesRow}
            visibleColumns={[
              'id',
              'date_start',
              'date_end',
              'datetime_created',
              'datetime_updated',
              'rule',
            ]}
            pageSize={pageSize}
            extraColumns={
              <>
                <TableCell sx={{ backgroundColor: 'white' }}></TableCell>
              </>
            }
            total={total}
            rowProps={{
              handleAction: handleRowAction,
            }}
          />
        </Card>
      </Box>
    </>
  )
}

function AddressOverrideWrapper() {
  return (
    <DataListLayout>
      <AddressOverrides />
    </DataListLayout>
  )
}

export default AddressOverrideWrapper
